import { deleteData, getData, handleErrors, postData } from "./common.service";
import { CacheConfig, getDynamicCacheConfig } from "../utilities/cachingConfig";
import { buildSensorDataQuery, TimeRangeSelection } from "../utilities/timeRange";

export async function getPlots(
  text: string = "",
  plotIds: string[] = [],
  skip = 0,
  filterCriteria?: any
) {
  const qs: any = {
    where: {
      or: [
        { plotId: { like: `.*${text}.*`, options: "i" } },
        { farmId: { like: `.*${text}.*`, options: "i" } },
        { name: { like: `.*${text}.*`, options: "i" } },
        { place: { like: `.*${text}.*`, options: "i" } },
        { taluka: { like: `.*${text}.*`, options: "i" } },
        { district: { like: `.*${text}.*`, options: "i" } },
        { farmUserId: { like: `.*${text}.*`, options: "i" } },
        { farmName: { like: `.*${text}.*`, options: "i" } },
        { farmerName: { like: `.*${text}.*`, options: "i" } },
        { farmerMobile: { like: `.*${text}.*`, options: "i" } },
        { deviceType: { like: `.*${text}.*`, options: "i" } },
        { deviceBatchId: { like: `.*${text}.*`, options: "i" } },
        { deviceIMEI: { like: `.*${text}.*`, options: "i" } },
        { tags: { like: `.*${text}.*`, options: "i" } },
        { orgId: { like: `.*${text}.*`, options: "i" } },
      ],
    },
    fields: [
      "plotId", "name",
      "place", "taluka", "district", "state", "location",
      "farmId", "farmName",
      "farmUserId", "farmerName", "farmerMobile",
      "deviceType", "deviceBatchId", "deviceIMEI",
      "cropId", "cropName",
      "tags", "orgId",
      "plotIsRecharged", "plotExpiryDate", "isSubscriptionActive", "subscriptionExpiryDate",
      "created_date", "servicedBy", "isHiddenByFarmer"
    ],
    order: "created_date DESC",
    limit: 20,
    skip: skip,
  };

  // If we have filter criteria, apply it
  if (filterCriteria && filterCriteria.where) {
    if (qs.where.or && qs.where.or.length > 0) {
      // Combine search text with filter criteria
      qs.where = {
        and: [
          filterCriteria.where,
          qs.where
        ]
      };
    } else {
      // Only filter criteria, no search text
      qs.where = filterCriteria.where;
    }
  } else if (plotIds.length) {
    // Original plotIds logic
    qs.where = {
      and: [
        {
          plotId: { inq: plotIds },
        },
        qs.where,
      ],
    };
  }

  return getData("/plots?filter=" + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => data.json());
}

export async function getPlotIdsByFilter(filter: any) {
  return getData("/plots?filter=" + JSON.stringify(filter), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => data.json());
}

export async function getPlot(plotId: string) {
  const qs = {
    include: ["device", "cropSown"],
  };
  return getData(`/plots/${plotId}` + "?filter=" + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => data.json());
}

export async function searchPlot(plotId: string) {
  const qs = {
    fields: {
      plotId: true,
    },
  };
  return getData(`/plots/${plotId}?filter=` + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  })
    .then((data) => data.json())
    .catch((e) => console.log(e));
}

export async function getPlotTypes() {
  return getData(
    "/plots/types",
    {
      sendTokenInHeader: true,
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    },
    CacheConfig.PLOT_TYPES.cacheKey,
    CacheConfig.PLOT_TYPES.cacheTime
  ).then((data) => data);
}

export async function getAvailableDevicesPaginated(
  text: string = "",
  skip: number = 0,
  limit: number = 20
) {
  const qs = {
    where: {
      imei: {
        like: `.*${text}.*`,
      },
      isAssigned: false,
    },
    limit: limit,
    skip: skip,
    fields: {
      imei: true,
      deviceId: true,
      deviceType: true,
      sensorId: true,
    },
    order: ["imei ASC"],
  };

  return getData("/devices?filter=" + JSON.stringify(qs), {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => {
    return data.json().then((jsonData: any[]) => {
      const modifiedData = jsonData.map((item) => {
        const name = item.imei;
        return { ...item, name };
      });
      return modifiedData;
    });
  });
}

export async function getAssignedDevice(imei: string) {
  const qs = {
    where: {
      isAssigned: true,
      imei: imei,
    },
    limit: 1,
  };
  return getData("/devices?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((data) => data.json());
}

// The server owns what an unassign resets (learnt soil calibration, Auro motorConfig), so the
// client only names the device. Throws on any non-2xx — postData hands back the raw Response,
// so callers used to see "success" for a 403/409/500.
export async function unassignIMEI(imei: string) {
  const response = await postData(`/devices/${encodeURIComponent(imei)}/unassign`, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      Accept: "application/json",
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error?.message || `Failed to unassign IMEI (${response.status})`);
  }
  return response.json();
}
export async function assignIMEI(imei: string) {
  if (!/^\d{10,20}$/.test(imei)) {
    return;
  }
  const qs = {
    isAssigned: false,
    imei: imei,
  };
  return postData("/devices?where=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    usePatchMethod: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: {
      isAssigned: true,
    },
  })
    .then((data) => {
      data.json();
    })
    .catch((err) => console.log(err));
}

export async function transferToFarm(plotId: string, farmId: string) {
  if (!plotId || !farmId) {
    console.log("Invalid plotId or FarmId");
    throw new Error("Invalid plotId or FarmId");
  }
  return postData(`/plots/${plotId}/transfer`, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: {
      farmId: farmId,
    },
  }).then(handleErrors);
}

export async function getSupportedCrops() {
  return getData(
    "/crops/supported",
    {
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
      sendTokenInHeader: true,
    },
    CacheConfig.CROPS.cacheKey,
    CacheConfig.CROPS.cacheTime
  ).then((data) => data);
}

export async function getSupportedRootStocks(cropId: string) {

  const dynamicCacheConfig = getDynamicCacheConfig("RootStocks", cropId);

  return getData(
    `/crops/${cropId}/root-stocks`,
    {
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
      sendTokenInHeader: true,
    },
    dynamicCacheConfig.cacheKey,
    dynamicCacheConfig.cacheTime

  ).then((data) => data);
}

export async function getCropVarieties(cropId: string) {
  const dynamicCacheConfig = getDynamicCacheConfig("CropVarieties", cropId);
  return getData(
    `/crops/${cropId}/crop-varieties`,
    {
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
      sendTokenInHeader: true,
    },
    dynamicCacheConfig.cacheKey,
    dynamicCacheConfig.cacheTime
  ).then((data) => data);
}

export async function getSensors(deviceType: string) {
  const dynamicCacheConfig = getDynamicCacheConfig("Sensors", deviceType);
  return getData(
    `/devices/${deviceType}/sensors`,
    {
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
      sendTokenInHeader: true,
    },
    dynamicCacheConfig.cacheKey,
    dynamicCacheConfig.cacheTime
  ).then((data) => data);
}

export async function getNearestPlots(
  location: any,
  where?: Record<string, any>,
  limit = 50,
  maxDistance?: number,
) {
  return postData("/plots/nearest", {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: {
      latitude: location?.latitude,
      longitude: location?.longitude,
      limit,
      where,
      ...(maxDistance ? { maxDistance } : {}),
    },
  }).then((data) => data.json());
}

/**
 * "Plots near me" for the Faulty Plots page.
 *
 * Reuses POST /plots/nearest ($geoNear on plot.geoLocation) so results come
 * back already sorted by distance with a `distanceInMeters` field that
 * PlotCard renders. `where` is passed straight to MongoDB's $geoNear.query,
 * so it MUST use native Mongo operators ($ne / $in) — not LoopBack ones.
 */
async function getFaultyPlotsNearMe(
  flag: "inactive" | "outlier",
  userProfile: any,
  location: { latitude: number; longitude: number },
  maxDistanceKm = 100,
  limit = 150,
): Promise<any[]> {
  const userRegions: string[] = userProfile?.regionIds || [];

  const where: Record<string, any> = {
    deviceType: { $ne: null },
    plotIsRecharged: true,
    [flag]: true,
  };

  // /plots/nearest does not run addPlotFilter, so mirror the region scoping
  // that getPlotsByFilter applies — otherwise a regional user's "near me"
  // would surface plots outside their territory.
  if (userRegions.length > 0) {
    where.regionIds = { $in: userRegions };
  }

  const results = await getNearestPlots(
    location,
    where,
    limit,
    maxDistanceKm * 1000,
  );
  // postData does not throw on non-2xx — the body comes back as an error
  // object, not an array. Surface that instead of showing "0 plots nearby".
  if (!Array.isArray(results)) {
    throw new Error(
      results?.error?.message || results?.error || "Could not fetch nearby plots.",
    );
  }
  return results;
}

export async function getInactivePlotsNearMe(
  userProfile: any,
  location: { latitude: number; longitude: number },
  maxDistanceKm?: number,
  limit?: number,
): Promise<any[]> {
  return getFaultyPlotsNearMe(
    "inactive",
    userProfile,
    location,
    maxDistanceKm,
    limit,
  );
}

export async function getOutlierPlotsNearMe(
  userProfile: any,
  location: { latitude: number; longitude: number },
  maxDistanceKm?: number,
  limit?: number,
): Promise<any[]> {
  return getFaultyPlotsNearMe(
    "outlier",
    userProfile,
    location,
    maxDistanceKm,
    limit,
  );
}

/**
 * Client-side text match for the "near me" list (the nearby set is already
 * bounded, so we filter it in memory instead of round-tripping).
 */
export function searchPlotsClientSide(plots: any[], text = ""): any[] {
  const q = text.trim().toLowerCase();
  if (!q) return plots;
  return plots.filter((p: any) =>
    [
      p?.plotId,
      p?.name,
      p?.place,
      p?.taluka,
      p?.district,
      p?.farmName,
      p?.farmerName,
      p?.farmerMobile,
      p?.deviceType,
    ].some((v) => typeof v === "string" && v.toLowerCase().includes(q)),
  );
}

export async function registerPlot(plotData: any) {
  return postData("/plots/add", {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: plotData,
  });
}

export async function updatePlot(plotData: any) {
  return postData("/plots/update", {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: plotData,
  });
}
export async function updateAssignOfPlot(plotData: any) {
  const dataToSend = {
    uninstallationDate: plotData.uninstallationDate,
    uninstallationReason: plotData.uninstallationReason,
  };
  return postData(`/plots/${plotData.plotId}`, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    usePatchMethod: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: dataToSend,
  });
}

export async function getInactivePlots(
  sinceHours: number,
  userProifle: any,
  geolocation?: any,
  plotIds?: string[]
) {
  const userRegions: string[] = userProifle?.regionIds || [];
  let url = `/inactive-devices?sinceHours=${sinceHours}`;
  if (userRegions?.length) {
    url += `&regionIds=${userRegions?.join(",")}`;
  }
  if (
    geolocation &&
    geolocation?.coords?.latitude &&
    geolocation?.coords?.longitude
  ) {
    url += `&latitude=${geolocation?.coords?.latitude}&longitude=${geolocation?.coords?.longitude}`;
  }
  if (plotIds && plotIds.length > 0) {
    const queryString = {
      where: {
        plotId: { in: plotIds },
      },
    };
    url +=
      (url.includes("?") ? `&` : `?`) + `filter=${JSON.stringify(queryString)}`;
  }
  return getData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  })
    .then((data) => data.json())
    .catch((e) => []);
}

export async function getExpiredPlots(
  userProfile?: any,
  geolocation?: any,
  plotIds?: string[]
) {
  const userRegions: string[] = userProfile?.regionIds || [];

  let url = `/expired-plots`;

  if (geolocation?.coords?.latitude && geolocation?.coords?.longitude) {
    url += `?latitude=${geolocation?.coords?.latitude}&longitude=${geolocation?.coords?.longitude}`;
  }

  if (userRegions?.length) {
    url += `?regionIds=${userRegions?.join(",")}`;
  }

  if (!!plotIds && plotIds.length > 0) {
    const queryString = {
      where: {
        plotId: { in: plotIds },
      },
    };
    url +=
      (url.includes("?") ? `&` : `?`) + `filter=${JSON.stringify(queryString)}`;
  }
  return getData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((data) => data.json());
}

/**
 * Common helper function to get plot count by filter criteria
 * Supports search text and additional filter criteria from PlotFilter
 */
async function getPlotsCountByFilter(
  baseFilterCriteria: Record<string, any>[],
  userProfile: any,
  searchText: string = "",
  additionalFilterCriteria?: any
): Promise<number> {
  const userRegions: string[] = userProfile?.regionIds || [];

  const filter: any = {
    where: {
      and: [
        { deviceType: { ne: null } },
        ...baseFilterCriteria
      ]
    }
  };

  // Add text search if provided (same pattern as getPlotsByFilter)
  if (searchText && searchText.trim()) {
    const textSearchFilter = {
      or: [
        { plotId: { like: `.*${searchText}.*`, options: "i" } },
        { name: { like: `.*${searchText}.*`, options: "i" } },
        { place: { like: `.*${searchText}.*`, options: "i" } },
        { taluka: { like: `.*${searchText}.*`, options: "i" } },
        { district: { like: `.*${searchText}.*`, options: "i" } },
        { farmUserId: { like: `.*${searchText}.*`, options: "i" } },
        { farmName: { like: `.*${searchText}.*`, options: "i" } },
        { farmerName: { like: `.*${searchText}.*`, options: "i" } },
        { farmerMobile: { like: `.*${searchText}.*`, options: "i" } },
        { deviceIMEI: { like: `.*${searchText}.*`, options: "i" } },
      ]
    };
    filter.where.and.push(textSearchFilter);
  }

  // Add additional filter criteria from PlotFilter
  if (additionalFilterCriteria?.where?.and?.length > 0) {
    filter.where.and.push(...additionalFilterCriteria.where.and);
  }

  // Add region filter if user has regionIds
  if (userRegions.length > 0) {
    filter.where.and.push({
      regionIds: { inq: userRegions }
    });
  }

  try {
    const response = await postData(
      `/plots/filter/count`,
      {
        basePath: process.env.REACT_APP_CORE_API_BASEPATH,
        sendTokenInHeader: true,
        body: filter,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    const data = await response.json();
    return data?.count ?? 0;
  } catch (e) {
    console.error("Error fetching plots count:", e);
    return 0;
  }
}

/**
 * Get count of inactive plots with search and filter support
 */
export async function getInactivePlotsCount(
  userProfile: any,
  searchText: string = "",
  filterCriteria?: any
): Promise<number> {
  return getPlotsCountByFilter([{ inactive: true, plotIsRecharged: true }], userProfile, searchText, filterCriteria);
}

/**
 * Get count of expired/non-recharged plots with search and filter support
 */
export async function getExpiredPlotsCount(
  userProfile: any,
  searchText: string = "",
  filterCriteria?: any
): Promise<number> {
  return getPlotsCountByFilter([{ plotIsRecharged: false }], userProfile, searchText, filterCriteria);
}

/**
 * Get paginated list of expired/non-recharged plots with search and filter support
 */
export async function getExpiredPlotsList(
  userProfile: any,
  skip: number = 0,
  limit: number = 20,
  searchText: string = "",
  filterCriteria?: any
): Promise<any[]> {
  return getPlotsByFilter(
    [{ plotIsRecharged: false }],
    userProfile,
    skip,
    limit,
    searchText,
    filterCriteria
  );
}

/**
 * Common helper function to get paginated plots by filter criteria
 * Supports server-side text search and additional filter criteria from PlotFilter
 */
async function getPlotsByFilter(
  baseFilterCriteria: Record<string, any>[],
  userProfile: any,
  skip: number = 0,
  limit: number = 20,
  searchText: string = "",
  additionalFilterCriteria?: any
): Promise<any[]> {
  const userRegions: string[] = userProfile?.regionIds || [];

  // Build base filter with required criteria
  const filter: any = {
    where: {
      and: [
        { deviceType: { ne: null } },
        ...baseFilterCriteria
      ]
    },
    skip,
    limit,
    order: "created_date DESC"
  };

  // Add text search if provided (same pattern as getPlots)
  if (searchText && searchText.trim()) {
    const textSearchFilter = {
      or: [
        { plotId: { like: `.*${searchText}.*`, options: "i" } },
        { name: { like: `.*${searchText}.*`, options: "i" } },
        { place: { like: `.*${searchText}.*`, options: "i" } },
        { taluka: { like: `.*${searchText}.*`, options: "i" } },
        { district: { like: `.*${searchText}.*`, options: "i" } },
        { farmUserId: { like: `.*${searchText}.*`, options: "i" } },
        { farmName: { like: `.*${searchText}.*`, options: "i" } },
        { farmerName: { like: `.*${searchText}.*`, options: "i" } },
        { farmerMobile: { like: `.*${searchText}.*`, options: "i" } },
        { deviceIMEI: { like: `.*${searchText}.*`, options: "i" } },
      ]
    };
    filter.where.and.push(textSearchFilter);
  }

  // Add additional filter criteria from PlotFilter
  if (additionalFilterCriteria?.where?.and?.length > 0) {
    filter.where.and.push(...additionalFilterCriteria.where.and);
  }

  // Add region filter if user has regionIds
  if (userRegions.length > 0) {
    filter.where.and.push({
      regionIds: { inq: userRegions }
    });
  }

  try {
    const response = await postData(
      `/plots/filter`,
      {
        basePath: process.env.REACT_APP_CORE_API_BASEPATH,
        sendTokenInHeader: true,
        body: filter,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    const data = await response.json();
    return data ?? [];
  } catch (e) {
    console.error("Error fetching plots:", e);
    return [];
  }
}

/**
 * Get paginated list of inactive plots with search and filter support
 */
export async function getInactivePlotsList(
  userProfile: any,
  skip: number = 0,
  limit: number = 20,
  searchText: string = "",
  filterCriteria?: any
): Promise<any[]> {
  return getPlotsByFilter(
    [{ inactive: true, plotIsRecharged: true }],
    userProfile,
    skip,
    limit,
    searchText,
    filterCriteria
  );
}

/**
 * Get count of outlier plots with search and filter support
 */
export async function getOutlierPlotsCount(
  userProfile: any,
  searchText: string = "",
  filterCriteria?: any
): Promise<number> {
  return getPlotsCountByFilter([{ outlier: true, plotIsRecharged: true }], userProfile, searchText, filterCriteria);
}

/**
 * Get paginated list of outlier plots with search and filter support
 */
export async function getOutlierPlotsList(
  userProfile: any,
  skip: number = 0,
  limit: number = 20,
  searchText: string = "",
  filterCriteria?: any
): Promise<any[]> {
  return getPlotsByFilter(
    [{ outlier: true, plotIsRecharged: true }],
    userProfile,
    skip,
    limit,
    searchText,
    filterCriteria
  );
}

export async function getOutlierPlots(
  sinceHours: number,
  userProifle: any,
  geolocation: any,
  plotIds: string[] = []
) {
  const userRegions: string[] = userProifle?.regionIds || [];
  let url = `/get-anomalous-plots?sinceHours=${sinceHours}`;
  if (userRegions?.length) {
    url += `&regionIds=${userRegions?.join(",")}`;
  }
  if (geolocation?.coords?.latitude && geolocation?.coords?.longitude) {
    url += `&latitude=${geolocation?.coords?.latitude}&longitude=${geolocation?.coords?.longitude}`;
  }
  if (plotIds.length > 0) {
    const queryString = {
      where: {
        plotId: { in: plotIds },
      },
    };
    url +=
      (url.includes("?") ? `&` : `?`) + `filter=${JSON.stringify(queryString)}`;
  }
  return getData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  })
    .then((data) => data.json())
    .catch((e) => []);
}

export async function getPlotSensorData(plotId: string, sinceHours: number | TimeRangeSelection) {
  const query =
    typeof sinceHours === "number"
      ? `?sinceHours=${sinceHours}`
      : buildSensorDataQuery(sinceHours);
  const url = `/sensor-data/${plotId}${query}`;
  return getData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then(async (res) => {
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(data?.error?.message || `Failed to fetch sensor data (${res.status})`);
    }
    return data;
  });
}

export function filterPlots(plots: any[], searchText: string = "") {
  return plots.filter(
    (item: any) =>
      item?.plotId?.toLowerCase().includes(searchText.toLowerCase()) ||
      item?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      item?.place?.toLowerCase().includes(searchText.toLowerCase()) ||
      item?.farm?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      item?.farm?.farmUser?.name
        .toLowerCase()
        .includes(searchText.toLowerCase())
  );
}

export async function getPlotIdsByCrops(cropIds: string[]) {
  const qs = {
    where: {
      cropId: {
        inq: cropIds,
      },
    },
    fields: ["plotId"],
  };
  return getData("/plots?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((data) => data.json());
}

export async function getPlotIdsByTags(tags: string[]) {
  const qs: any = {
    where: {
      tags: { inq: tags },
    },
    fields: ["plotId"],
  };
  return getData("/plots?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((data) => data.json());
}

export async function getPlotTags() {
  return getData(
    `/plots/tags`,
    {
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
      sendTokenInHeader: true,
    },
    CacheConfig.PLOT_TAGS.cacheKey,
    CacheConfig.PLOT_TAGS.cacheTime
  ).then((data) => data);
}

export async function postpubliclyAccessible(
  plotId: string,
  publiclyAccessible: boolean
) {
  return postData(`/plots/${plotId}`, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    usePatchMethod: true,
    body: {
      publiclyAccessible: publiclyAccessible,
    },
  });
}

export async function updatePlotDetails(plotId: string, payload: any) {
  const url = `/plots/${plotId}`;

  return postData(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    body: payload,
    usePatchMethod: true,
  });
}

// Puts a plot the farmer removed (isHiddenByFarmer) back into their app. Goes through PATCH because
// POST /plots/{id}/unhide is owner/admin only; PATCH lets admin/support set the flag.
export async function unhidePlotById(plotId: string) {
  return updatePlotDetails(plotId, { isHiddenByFarmer: false });
}

// Admin: real delete. Anyone else (support): the backend only hides the plot from the farmer's app.
export async function deletePlotById(plotId: string) {
  const url = `/plots/${plotId}`;
  return deleteData(url, {
    headers: {
      Accept: "application/json",
    },
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  });
}

export async function reCalibrateDeviceById(plotId: string, startDate?: string) {
  const url = startDate
    ? `/plots/${plotId}/recalibrate?startDate=${encodeURIComponent(startDate)}`
    : `/plots/${plotId}/recalibrate`;
  return postData(url, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  });
}
export async function getAiFilteredPlot(query: string) {
  return getData("/plots/similar?query=" + query, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  }).then((data) => data.json());
}

export async function getSoilTypes() {
  const qs = {
    order: "order ASC",
  };
  return getData(
    "/soils/?filter=" + JSON.stringify(qs),
    {
      sendTokenInHeader: true,
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    },
    CacheConfig.NEW_SOIL_TYPES.cacheKey,
    CacheConfig.NEW_SOIL_TYPES.cacheTime
  ).then((data) => data);

}
